<template>
  <ValidationObserver ref="observer">
    <template #default="{ handleSubmit, invalid }">
      <form class="text-sm" @submit.prevent="handleSubmit(doSubmit)">
        <ul class="flex border-b-2 select-none">
          <li
            class="flex-1 text-center -mb-2px py-3 border-b-2 text-base font-medium"
            :class="[
              isRecipientTab ? 'border-blue-600' : '',
              isRecipientTab ? 'text-gray-900' : 'text-gray-600',
            ]"
            @click="focusRecipientTab"
          >
            Recipient Details
          </li>
          <li
            class="flex-1 text-center -mb-2px py-3 border-b-2 text-base font-medium"
            :class="[
              isIssuerTab ? 'border-blue-600' : '',
              isIssuerTab ? 'text-gray-900' : 'text-gray-600',
            ]"
            @click="focusIssuerTab"
          >
            Issuer Details
          </li>
          <li
            class="flex-1 text-center -mb-2px py-3 border-b-2 text-base font-medium"
            :class="[
              isOfferTab ? 'border-blue-600' : '',
              isOfferTab ? 'text-gray-900' : 'text-gray-600',
            ]"
            @click="focusOfferTab"
          >
            Offer Details
          </li>
        </ul>

        <div class="flex -mx-4 mt-12">
          <div class="flex-1 px-4">
            <RecipientDetails
              v-show="isRecipientTab"
              key="recipient_details"
              :account-uuid="data.recipientUuid"
              :detail-options="recipientDetailOptions"
              :name.sync="data.recipientName"
              :address.sync="data.recipientAddress"
              :vat-number.sync="data.recipientVatNumber"
              :vat-office.sync="data.recipientVatOffice"
              :disabled="disabled"
            />

            <div v-show="isIssuerTab" key="issuer_details">
              <BaseFormGroup v-if="!data.uuid" class="mb-8">
                <BaseFormLabel> Account </BaseFormLabel>
                <VSelect
                  v-model="data.issuerUuid"
                  :options="issuerOptions"
                  :reduce="(issuer) => issuer.value"
                  class="vue-select w-64"
                />
              </BaseFormGroup>
              <IssuerDetails
                :account-uuid="data.issuerUuid"
                :detail-options="issuerDetailOptions"
                :name.sync="data.issuerName"
                :address.sync="data.issuerAddress"
                :vat-number.sync="data.issuerVatNumber"
                :vat-office.sync="data.issuerVatOffice"
                :disabled="disabled"
              />
            </div>

            <div v-show="isOfferTab" key="invoise_details">
              <OfferDetails
                :offer-issue-date.sync="data.issueDate"
                :offer-number.sync="data.number"
                :offer-type.sync="data.type"
                :disabled="disabled"
                :locked="hasUuid"
                class="mb-12"
              />
              <OfferLines :offer-lines.sync="dataLines" :disabled="disabled" />
            </div>
          </div>
        </div>

        <div
          v-if="$refs.observer && $refs.observer.ctx && $refs.observer.ctx.invalid"
          class="bg-red-100 text-red-600 rounded p-4 mt-16 border border-red-600"
        >
          <ul class="list-disc list-inside">
            <template v-for="(provider, key) in $refs.observer.refs">
              <li
                v-for="(message, errorIdx) in getProviderMessages(provider)"
                :key="`${key}_${errorIdx}`"
              >
                {{ message }}
              </li>
            </template>
          </ul>
        </div>

        <div class="mt-16 border-t pt-12 text-right">
          <BaseButton primary class="px-6" :disabled="disabled || invalid"> Submit </BaseButton>
        </div>
      </form>
    </template>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import RecipientDetailsVue from './offerForm/RecipientDetails.vue';
import IssuerDetailsVue from './offerForm/IssuerDetails.vue';
import OfferLinesVue from './offerForm/OfferLines.vue';
import OfferDetailsVue from './offerForm/OfferDetails.vue';

const TAB_RECIPIENT = 'recipient';
const TAB_ISSUER = 'issuer';
const TAB_OFFER = 'offer';

export default {
  components: {
    ValidationObserver,
    IssuerDetails: IssuerDetailsVue,
    RecipientDetails: RecipientDetailsVue,
    OfferLines: OfferLinesVue,
    OfferDetails: OfferDetailsVue,
  },

  props: {
    defaultData: {
      type: Object,
      required: true,
    },
    defaultLines: {
      type: Array,
      default: () => [],
    },
    recipientOptions: {
      type: Array,
      default: () => [],
    },
    issuerOptions: {
      type: Array,
      default: () => [],
    },
    serverErrors: {
      type: Object,
      default: () => ({}),
    },
    disabled: Boolean,
  },

  data() {
    return {
      currentTab: TAB_RECIPIENT,
      data: {
        issueDate: '',
        number: '',
        recipientName: '',
        recipientAddress: '',
        recipientVatNumber: '',
        recipientVatOffice: '',
        issuerUuid: null,
        issuerName: '',
        issuerAddress: '',
        issuerVatNumber: '',
        issuerVatOffice: '',
        ...this.defaultData,
      },
      dataLines: [...this.defaultLines],
    };
  },

  computed: {
    isRecipientTab() {
      return this.currentTab === TAB_RECIPIENT;
    },
    isIssuerTab() {
      return this.currentTab === TAB_ISSUER;
    },
    isOfferTab() {
      return this.currentTab === TAB_OFFER;
    },
    recipient() {
      return (
        this.recipientOptions.find((option) => option.value === this.data.recipientUuid) || null
      );
    },
    issuer() {
      return this.issuerOptions.find((option) => option.value === this.data.issuerUuid) || null;
    },
    recipientDetailOptions() {
      if (!this.recipient) return [];
      return this.recipient.registrations;
    },
    issuerDetailOptions() {
      if (!this.issuer) return [];
      return this.issuer.registrations;
    },
    hasUuid() {
      return typeof this.defaultData.uuid === 'string';
    },
  },

  watch: {
    defaultData: {
      deep: true,
      handler(newDefaultData) {
        this.data = { ...this.data, ...newDefaultData };
      },
    },
    defaultLines: {
      deep: true,
      handler(newDefaultLines) {
        this.dataLines = [...newDefaultLines];
      },
    },
    'data.issuerUuid': {
      handler() {
        this.setDefaultIssuerDetails();
      },
    },
    serverErrors: {
      deep: true,
      handler: 'applyServerErrors',
    },
  },

  methods: {
    focusRecipientTab() {
      this.currentTab = TAB_RECIPIENT;
    },
    focusIssuerTab() {
      this.currentTab = TAB_ISSUER;
    },
    focusOfferTab() {
      this.currentTab = TAB_OFFER;
    },
    setDefaultIssuerDetails() {
      if (this.issuerDetailOptions.length === 0) return;

      const defaultDetails = this.issuerDetailOptions.slice().pop();

      this.data.issuerName = defaultDetails.businessName;
      this.data.issuerAddress = defaultDetails.businessAddress;
      this.data.issuerVatNumber = defaultDetails.number;
      this.data.issuerVatOffice = defaultDetails.office;
    },
    doSubmit() {
      this.$emit('offer-form:submit', {
        ...this.data,
        offerLines: [...this.dataLines],
      });
    },
    getProviderMessages(provider) {
      if (!provider.messages) return [];

      return provider.messages;
    },
    applyServerErrors(errors) {
      this.observer = this.$refs.observer;
      this.addServerErrors(errors);
    },
  },
};
</script>
